import { Inject, Injectable, Renderer2 } from '@angular/core';
import { OlaMaps } from '@app/ola-maps';
import {
  ContainerItemOption,
  ContainerModel,
} from '@app/models/server/layout.model';
import { DOCUMENT } from '@angular/common';
import { EventService } from '@app/services/util-services/root-services/event-service';
import { environment } from '@src/environments/environment';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { ToastService } from '@app/services/util-services/root-services/toast-service';

@Injectable()
export class DetailPageOlaMapServices {
  private olaMaps: OlaMaps;
  private detailPageMap: any;
  private detailPageMapContainer: string;

  // isFullScreenMode: boolean = Boolean(false);
  // hideLandmarkSearchField: boolean = Boolean(false);

  private activePropertyMarkers: any[] = [];
  private activeLocalityMarkers: any[] = [];
  fullScreenButtonState: 'OPEN' | 'CLOSE_BUTTON' | 'ESC_BUTTON';
  routeData = {
    type: 'FeatureCollection',
    features: [],
  };

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private eventService: EventService,
    private b2bDeviceUtilService: B2bDeviceUtilService,
    private renderer: Renderer2,
    private toastService: ToastService,
  ) {}

  initOlaMaps(): OlaMaps {
    if (!this.olaMaps) {
      this.olaMaps = new OlaMaps({
        apiKey: environment.OLA.OLA_API_KEY,
      });
    }
    return this.olaMaps;
  }

  createDetailPageMap(
    containerName: string,
    center: { lat: number; lng: number },
    zoom: number,
  ) {
    try {
      if (!this.detailPageMapContainer) {
        this.detailPageMapContainer = containerName;
      }

      if (
        !this.detailPageMap ||
        this.detailPageMapContainer !== containerName
      ) {
        this.detailPageMap = this.olaMaps.init({
          style: environment.OLA.OLA_MAP_STYLE,
          container: containerName,
          // center: [center?.lng, center?.lat],
          zoom,
          fullscreenControl: false,
        });

        this.detailPageMapContainer = containerName;

        const navControl = new this.olaMaps.mapInstance.NavigationControl({
          showCompass: false, // Hide compass if not needed
          visualizePitch: true,
        });
        this.detailPageMap.addControl(navControl, 'bottom-right');

        // Recenter button
        this.addRecenterButton(center, zoom);

        if (this.b2bDeviceUtilService.isDesktop()) {
          // Expand/Collapse button
          // this.addExpandCollapseButton();
          this.setupCustomFullscreen();
        }
      }
      this.setMapCenter(center);
    } catch (e) {
      // console.warn('Error is ', e);
      if (!this.b2bDeviceUtilService.isWebglSupported()) {
        this.toastService.error(
          'Please enable hardware acceleration in your browser settings to view this content properly.',
          5000,
          'Graphics support disabled',
        );
      }
    }

    return this.detailPageMap;
  }

  setupCustomFullscreen() {
    // const mapWrapper = this.detailPageMapContainer.nativeElement.parentElement;
    const mapWrapper = this.document.getElementById(
      this.detailPageMapContainer,
    ).parentElement;

    // mapContainer.parentElement

    const fullscreenButton = this.document.createElement('button');
    fullscreenButton.className = 'custom-fullscreen-control';
    fullscreenButton.innerHTML = '⤢';

    // Listen for fullscreenchange event (triggered by ESC key as well)
    this.document.addEventListener('fullscreenchange', () => {
      // Update button or UI state based on fullscreen status
      if (this.document?.fullscreenElement) {
        // TODO if you want to do anything else
      } else {
        if (this.fullScreenButtonState === 'OPEN') {
          // exit fullscreen
          this.document
            ?.exitFullscreen()
            ?.then((r) => console.log('Exit full screen'))
            .catch((err) => {
              console.error(`Error while closing fullscreen: ${err.message}`);
            });

          const elements =
            this.document?.getElementsByClassName('map-container');
          if (elements?.length === 1) {
            this.renderer.removeClass(elements[0], 'fl-map');
          }
          this.fullScreenButtonState = 'ESC_BUTTON';
        }
      }
    });

    fullscreenButton.addEventListener('click', () => {
      try {
        if (!this.document?.fullscreenElement) {
          // Enter fullscreen mode
          this.fullScreenButtonState = 'OPEN';
          mapWrapper
            ?.requestFullscreen()
            ?.then(() => {
              // this.fullScreenButtonState = 'OPEN';
            })
            .catch((err) => {
              console.error(
                `Error attempting to enable fullscreen: ${err.message}`,
              );
            });

          const elements =
            this.document?.getElementsByClassName('map-container');
          if (elements?.length === 1) {
            this.renderer.addClass(elements[0], 'fl-map');
          }
        } else {
          // Exit fullscreen mode
          // this.exitingFullScreenByCustomButton = true;
          this.fullScreenButtonState = 'CLOSE_BUTTON';
          this.document
            ?.exitFullscreen()
            ?.then((r) => console.log('Exit full screen'))
            .catch((err) => {
              console.error(`Error while closing fullscreen: ${err.message}`);
            });

          const elements =
            this.document?.getElementsByClassName('map-container');
          if (elements?.length === 1) {
            this.renderer.removeClass(elements[0], 'fl-map');
          }
        }
      } catch (e) {
        // console.error('On fullscreen button click and error is ', e);
      }
    });

    // Add the button to the map
    const controlContainer = this.document.createElement('div');
    controlContainer.className =
      'maplibregl-ctrl maplibregl-ctrl-group expand-btn';
    controlContainer.appendChild(fullscreenButton);

    this.detailPageMap.getContainer().appendChild(controlContainer);

    // Style the control
    this.renderer.setStyle(controlContainer, 'position', 'absolute');
    this.renderer.setStyle(controlContainer, 'top', '18px');
    this.renderer.setStyle(controlContainer, 'right', '10px');
    this.renderer.setStyle(controlContainer, 'color', '#000000');
  }

  addRecenterButton(
    defaultCenter: { lat: number; lng: number },
    defaultZoom: number,
  ) {
    // Select the existing MapLibre navigation control
    const navControlElements = this.document.querySelectorAll(
      '.maplibregl-ctrl button',
    );

    if (navControlElements.length > 0) {
      // Create the Recenter Button (Inside Navigation Control)
      const recenterButton = this.document.createElement('button');
      /* recenterButton.innerHTML = '⮝';*/ // Unicode for a refresh/recenter symbol
      recenterButton.innerHTML =
        '<div><div class="compassIcon"><img src="https://static-img.bag2bag.in/new-assets/india/new-app/icons/map-recenter-icon.png" alt="map-compass" loading="lazy" width="64" height="64"></div></div>'; // Unicode for a refresh/recenter symbol
      recenterButton.title = 'Recenter Map';
      recenterButton.classList.add('maplibregl-ctrl-icon', 'recenter-icon');

      // Add click event to reset the map view
      recenterButton.onclick = () => {
        this.detailPageMap.flyTo({
          center: [defaultCenter.lng, defaultCenter.lat],
          zoom: defaultZoom,
          essential: true,
        });
      };

      // Insert recenter button next to zoom buttons
      const navigationContainer = navControlElements[0].parentElement; // Get the parent div
      if (navigationContainer) {
        navigationContainer.appendChild(recenterButton);
      }
    }
  }

  /*  addExpandCollapseButton() {
      // Select the existing MapLibre navigation control
      /!*const navControlElements = this.document.querySelectorAll(
        '.maplibregl-ctrl button',
      );*!/

      /!* this.olaMaps.addNavigationControls(
         new this.olaMaps.mapInstance.FullscreenControl(),
       );*!/

      const fullScreenControl = new this.olaMaps.mapInstance.FullscreenControl();

      fullScreenControl.on('fullscreenstart', () => {
        this.toggleFullScreen();
      });

      fullScreenControl.on('fullscreenend', () => {
        this.toggleFullScreen();
      });

      this.detailPageMap.addControl(fullScreenControl);

      /!*if (navControlElements.length > 0) {
        const expandButton = this.document.createElement('button');
        expandButton.id = 'map-exp-btn';
        expandButton.innerHTML = '⛶'; // Unicode for Expand icon
        expandButton.title = 'Expand Map';
        expandButton.classList.add('maplibregl-ctrl-icon');

        expandButton.onclick = () => {
          this.toggleFullScreen();
        };

        // Insert the Expand/Collapse button inside the navigation control
        const navigationContainer = navControlElements[0].parentElement;
        if (navigationContainer) {
          navigationContainer.appendChild(expandButton);
        }
      }*!/
    }*/

  /*
    toggleFullScreen() {
      this.isFullScreenMode = !this.isFullScreenMode;
      this.hideLandmarkSearchField = true;
      setTimeout(() => {
        this.hideLandmarkSearchField = false;
      }, 1000);

      // let isExpanded = false;
      /!*const mapContainer = this.document.getElementById(
        this.detailPageMapContainer,
      );*!/
      /!*const expandButton = this.document.getElementById('map-exp-btn');
      if (mapContainer) {
        if (!this.isFullScreenMode) {
          mapContainer.style.position = 'fixed';
          mapContainer.style.zIndex = '999';
          mapContainer.style.top = '0';
          mapContainer.style.left = '0';
          mapContainer.style.width = '100vw';
          mapContainer.style.height = '100vh';
          if (expandButton) {
            expandButton.innerHTML = '⤢'; // Unicode for Collapse icon
            expandButton.title = 'Collapse Map';
          }
          this.addCloseButton(mapContainer);
          this.showCloseButton();
        } else {
          mapContainer.style.position = '';
          mapContainer.style.zIndex = '';
          mapContainer.style.width = '';
          mapContainer.style.height = '';
          if (expandButton) {
            expandButton.innerHTML = '⛶';
            expandButton.title = 'Expand Map';
          }
          this.hideCloseButton();
        }

        // this.detailPageMap.resize();
      }*!/
    }
  */

  /*hideCloseButton() {
    let closeButton = this.document.getElementById('map-close-btn');
    if (closeButton) {
      closeButton.style.display = 'none';
    }
  }*/

  /*showCloseButton() {
    let closeButton = this.document.getElementById('map-close-btn');
    if (closeButton) {
      closeButton.style.display = 'block';
    }
  }*/

  /*addCloseButton(mapContainer: HTMLElement) {
    let closeButton = this.document.getElementById('map-close-btn');

    if (!closeButton) {
      closeButton = this.document.createElement('button');
      closeButton.id = 'map-close-btn';
      closeButton.innerHTML = '✖'; // Close icon
      closeButton.title = 'Close Map';
      closeButton.style.position = 'absolute';
      closeButton.style.top = '43px';
      closeButton.style.right = '15px';
      closeButton.style.zIndex = '1000';
      closeButton.style.background = 'white';
      closeButton.style.border = 'none';
      closeButton.style.color = '#3a3a3a';
      closeButton.style.padding = '6px 8px';
      closeButton.style.fontSize = '16px';
      closeButton.style.cursor = 'pointer';
      closeButton.style.borderRadius = '5px';
      closeButton.style.boxShadow = '0px 2px 5px rgba(0,0,0,0.3)';

      closeButton.onclick = () => {
        this.toggleFullScreen();
      };

      mapContainer.appendChild(closeButton);
    }
  }*/

  setMapCenter(center: { lat: number; lng: number }) {
    this.detailPageMap.setCenter(center);
  }

  /*  resetMapWithNewData(containers: ContainerModel[], locations: SelectedItem[]) {
      if (this.activePropertyMarkers?.length > 0) {
        for (const marker of this.activePropertyMarkers) {
          this.removeMarker(marker);
        }
      }

      this.activePropertyMarkers = [];

      if (this.activeLocalityMarkers?.length > 0) {
        for (const marker of this.activeLocalityMarkers) {
          this.removeMarker(marker);
        }
      }
      this.activeLocalityMarkers = [];
    }*/

  /* getMapZoom(): number {
     return this.detailPageMap?.getZoom();
   }*/

  moveTo(newCenter: { lat: number; lng: number }, zoom: number = 13) {
    this.detailPageMap.flyTo({
      center: [newCenter?.lng, newCenter?.lat],
      zoom,
      // speed: 0.2,
      curve: 1,
      easing(t) {
        return t;
      },
    });
  }

  setLandmarkMarkerAndRoute(
    totalDistance: string,
    toPlace: ContainerItemOption,
    fromPlaceCenter: { lat: number; lng: number },
    geometry: {
      type: string;
      coordinates: number[][];
    },
  ) {
    const toPlaceCenter: { lat: number; lng: number } = {
      lat: toPlace?.location?.coordinates?.[1],
      lng: toPlace?.location?.coordinates?.[0],
    };

    this.setLocationMarker(
      toPlaceCenter,
      toPlace?._id as string,
      toPlace?.title,
    );
    this.drawRoute(totalDistance, toPlaceCenter, fromPlaceCenter, geometry);
  }

  setLocationMarker(
    newCenter: { lat: number; lng: number },
    locationId: string,
    locationName: string,
  ) {
    const prefix: string = String('loc-marker-');
    if (newCenter?.lat && newCenter?.lng) {
      const iconDiv = this.document?.getElementById(prefix + locationId);

      if (iconDiv) {
        return;
      }

      const icon = this.document?.createElement('div');
      icon.id = prefix + locationId;

      // icon.innerText = locationName?.substring(0, 2);

      icon.classList.add('location-mk-icon');
      icon.style.backgroundImage = `url('https://static-img.bag2bag.in/new-assets/india/new-app/icons/map-pin-v2.png')`;
      icon.style.width = '35px';
      icon.style.height = '35px';
      icon.style.backgroundSize = 'contain';
      icon.style.backgroundRepeat = 'no-repeat';
      // icon.style.backgroundColor = 'white';
      // icon.style.borderRadius = '50px';

      // TODO
      const popupContent = `
        <div>${locationName}</div>
      `;

      icon.onclick = () => {
        // TODO incase if we want to zoom on locality or something
      };

      const popup = this.createPopup(
        { offset: [0, 0], anchor: 'bottom', className: 'dp-loc-nm' },
        popupContent,
      );

      this.addMarker(
        { element: icon, anchor: 'bottom', className: 'dp-loc-mk' },
        { lat: newCenter?.lat, lng: newCenter?.lng },
        popup,
        this.detailPageMap,
        'LOCALITY',
      );
    } else {
    }
  }

  setPropertyMarkers(containers: ContainerModel[]) {
    if (containers?.length > 0) {
      for (const container of containers) {
        this.setPropertyMarker(container);
      }
    }
  }

  setPropertyMarker(container: ContainerModel) {
    if (container?.lat && container?.lng) {
      const iconDiv = this.document?.getElementById(
        'marker-con-' + container?.uid,
      );

      if (iconDiv) {
        return;
      }

      const propertyIconContainer = this.document.createElement('div');
      propertyIconContainer.className = 'jumping-marker-container';
      propertyIconContainer.id = 'marker-con-' + container?.uid;

      const propertyIcon = this.document?.createElement('div');
      propertyIcon.id = 'marker-' + container?.uid;

      // propertyIcon.innerText = `Selected property`;

      propertyIcon.classList.add('property-icon');

      // propertyIcon.style.backgroundImage = `url('/assets/images/bed.png')`;
      propertyIcon.style.backgroundImage = `url('https://static-img.bag2bag.in/new-assets/india/new-app/logo/b2b-fav-icon.webp')`;
      propertyIcon.style.width = '36px';
      propertyIcon.style.height = '36px';
      propertyIcon.style.backgroundSize = 'contain';
      propertyIcon.style.backgroundRepeat = 'no-repeat';
      // propertyIcon.style.backgroundColor = 'white';
      // propertyIcon.style.borderRadius = '50px';
      propertyIcon.style.animation = 'markerJump 1s infinite';

      // TODO
      const popupContent = `
        <div>Name: ${container?.displayName}</div>
        <div>Remove tooltip in future after testing</div>
      `;

      propertyIcon.onclick = () => {
        // this.onSelectPropertyMarker(container);
        this.eventService.markerClickEvent.emit({
          container,
        });
      };

      const popup = this.createPopup(
        { offset: [0, 0], anchor: 'bottom' },
        popupContent,
      );

      // Add marker to container
      propertyIconContainer.appendChild(propertyIcon);

      this.addMarker(
        { element: propertyIconContainer, anchor: 'bottom' },
        { lat: container?.lat, lng: container?.lng },
        null,
        this.detailPageMap,
        'PROPERTY',
      );
    } else {
    }
  }

  createPopup(options, htmlContent) {
    return this.olaMaps?.addPopup(options)?.setHTML(htmlContent);
  }

  /*removeMarker(marker) {
    marker?.remove();
  }*/

  addMarker(
    options,
    latLng: { lat: number; lng: number },
    popup: any,
    addToMap,
    markerType: 'PROPERTY' | 'LOCALITY',
  ) {
    try {
      const marker = this.olaMaps?.addMarker(options);

      marker?.setLngLat([latLng?.lng, latLng?.lat]);
      marker?.setPopup(popup);
      marker?.addTo(addToMap);
      if (markerType === 'PROPERTY') {
        this.activePropertyMarkers.push(marker);
        return marker;
      } else if (markerType === 'LOCALITY') {
        this.activeLocalityMarkers.push(marker);
        return marker;
      } else {
      }
    } catch (e) {}

    return null;
  }

  drawRoute(
    distance: string,
    toPlaceCenter: { lat: number; lng: number },
    fromPlaceCenter: { lat: number; lng: number },
    geometry: {
      type: string;
      coordinates: number[][];
    },
  ) {
    this.routeData.features.push({
      type: 'Feature',
      /*geometry: {
        type: 'LineString',
        coordinates,
      },*/
      geometry,
      properties: {},
    });

    if (this.detailPageMap.getSource('route-source')) {
      this.detailPageMap.getSource('route-source').setData(this.routeData);
    } else {
      this.detailPageMap.addSource('route-source', {
        type: 'geojson',
        data: this.routeData,
      });
      this.detailPageMap.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route-source',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#FF6B00',
          'line-width': 6,
          'line-opacity': 0.8,
        },
      });
    }

    // Calculate bounds that include both start and end points
    const bounds = new this.olaMaps.mapInstance.LngLatBounds();
    bounds.extend([fromPlaceCenter.lng, fromPlaceCenter.lat]);
    bounds.extend([toPlaceCenter.lng, toPlaceCenter.lat]);

    // Fit the map to show the entire route with padding
    this.detailPageMap.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 },
      duration: 1000,
      maxZoom: 15, // Prevent excessive zoom when points are close together
    });

    // Add a popup at the middle of the route
    const routePoints = geometry?.coordinates;
    const midPoint = routePoints[Math.floor(routePoints.length / 2)];

    // Wait for the bounds animation to complete before showing the popup
    setTimeout(() => {
      const popup = new this.olaMaps.mapInstance.Popup({
        closeButton: true,
        closeOnClick: false,
        className: 'route-info-popup',
        anchor: 'top',
        offset: [0, 10],
        focusAfterOpen: false,
      })
        .setLngLat(midPoint)
        .setHTML(
          `
            <div class="text-sm font-medium">
              <div class="dp-ds">📍${distance}</div>
            </div>
          `,
        );

      // Only add the popup if the map is still available
      if (this.detailPageMap) {
        popup.addTo(this.detailPageMap);
      }
    }, 2000);
  }

  destroy() {
    this.olaMaps = undefined;
    this.detailPageMap = undefined;
    this.detailPageMapContainer = undefined;
  }
}
